// /intel — Live Intelligence Layer.
//
// Iron Rule 11 compliant: this surface is a thin shell around `EntityScreen`
// + `intelFilterSchema`. Filter rail, IntentBar, ResultMetaBar and AI rail
// all come from the platform. The only Intel-specific pieces are:
//
//   * `IntelViewTabs` — segmented control routing between map / timeline / analytics.
//   * `MapView` — wraps WorldMap (deck.gl, registry-driven), AOI tool, time scrubber.
//   * `Timeline` (Phase C-2) — virtualized signal tape.
//   * `Analytics` (Phase C-2) — six-tile ECharts grid (lazy-loaded).
//   * `AlertTape` (Phase C-1) — sticky-bottom Bloomberg-style live ticker.
//
// One filter state, three views, zero re-fetch on switch. Filter state is
// canonical in URL search params via TanStack Router.

import { apiFetch } from '@/lib/api';
import { API_BASE_URL } from '@/lib/env';
import { useFilterRailLabels } from '@/lib/useFilterRailLabels';
import { EmptyState } from '@/primitives';
import { AlertTape } from '@/screens/intel/AlertTape';
import { IntelViewTabs } from '@/screens/intel/IntelViewTabs';
import { MapView } from '@/screens/intel/MapView';
import { nextSignalTypeFromSubcats } from '@/screens/intel/url-state';
import { useContractsMap } from '@/screens/intel/useContractsMap';
import { useIntelSignalTypeFacet } from '@/screens/intel/useIntelFacetCounts';
import { useIntelGeomlessResults } from '@/screens/intel/useIntelGeomlessResults';
// NOTE: `GroupBy` / `GROUP_OPTIONS` are inlined here (instead of imported from
// Timeline.tsx) so the Timeline module can stay a clean dynamic-only import.
// A static `import { GROUP_OPTIONS } from '@/screens/intel/Timeline'` here
// pulls Timeline into the route's main chunk and Rollup refuses to split it
// (warning: "dynamically imported by … but also statically imported"). The
// chunk-splitting win is real — ~12 KB shifted off the intel route's eager
// payload. Keep these in sync with Timeline's internal copy.
type GroupBy = 'flat' | 'region' | 'type' | 'agency' | 'severity';
const GROUP_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: 'flat', label: 'FLAT' },
  { id: 'region', label: 'REGION' },
  { id: 'type', label: 'TYPE' },
  { id: 'agency', label: 'AGENCY' },
  { id: 'severity', label: 'SEVERITY' },
];
import { readAccessToken, useAuth } from '@/state/auth';
import type { filters as f, intel as i } from '@eidollon/contracts';
import { filters as fc, gdeltCodes, intelDomains as iDom, intel as ic } from '@eidollon/contracts';
import { INTEL_LAYERS, type RegionMetric, useIntelFeed } from '@eidollon/intel-feed-client';
import { EntityScreen, type SortState } from '@eidollon/ui';
import {
  type HierarchicalDomainOption,
  HierarchicalFilter,
  SavedView,
  type SavedViewFetcher,
  decode,
  encode,
} from '@eidollon/ui-filters';
import type { GeoPolygonEntry, GeoPolygonFacetValue } from '@eidollon/ui-filters';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

// Analytics view disabled for now (founder request 2026-06-04). Lazy import
// kept commented so re-enabling is a one-line restore.
// const Analytics = lazy(() =>
//   import('@/screens/intel/Analytics').then((m) => ({ default: m.Analytics })),
// );
// NewsSidePanel only renders on /intel and is below-the-fold — lazy-load.
const NewsSidePanel = lazy(() =>
  import('@/screens/intel/NewsSidePanel').then((m) => ({ default: m.NewsSidePanel })),
);
// Timeline only renders on the Timeline view tab — keep it off the map's
// initial bundle.
const Timeline = lazy(() =>
  import('@/screens/intel/Timeline').then((m) => ({ default: m.Timeline })),
);

/* ──────────────────────────────────────────────────────────────────────────
 *  URL state
 * ────────────────────────────────────────────────────────────────────────── */

const intelSearch = z.object({
  view: ic.IntelView.optional(),
  f: z.string().optional(), // base64url(JSON(FilterState))
  sort: z.string().optional(), // 'occurred_at:desc'
  group: z.enum(['flat', 'region', 'type', 'agency', 'severity']).optional(),
});
type IntelSearch = z.infer<typeof intelSearch>;

export const Route = createFileRoute('/_authed/intel')({
  validateSearch: intelSearch,
  component: IntelPage,
});

const TIME_PRESETS = [
  { label: '1h', fromOffsetMs: 60 * 60 * 1000 },
  { label: '24h', fromOffsetMs: 24 * 60 * 60 * 1000 },
  { label: '7d', fromOffsetMs: 7 * 24 * 60 * 60 * 1000 },
  { label: '30d', fromOffsetMs: 30 * 24 * 60 * 60 * 1000 },
];

// Entity-keyed signal types that carry no coordinates and therefore can never
// plot on the map — selecting one docks the right-side non-geo results card.
// Today that's OFAC sanctions (SAN); add future entity-based codes here.
const GEOMLESS_SIGNAL_TYPES: ReadonlySet<string> = new Set(['SAN']);

const LIVE_EVENT_BUFFER = 256;

// Adapter from SavedView's fetcher contract → the terminal's auth-aware
// apiFetch (handles JWT, refresh, 401 retry). Lives at module scope so the
// reference is stable across renders.
const savedViewFetcher: SavedViewFetcher = async ({ url, method, body }) =>
  apiFetch<unknown>(url, body !== undefined ? { method, body } : { method });

/* ──────────────────────────────────────────────────────────────────────────
 *  Page
 * ────────────────────────────────────────────────────────────────────────── */

function IntelPage() {
  const navigate = useNavigate({ from: '/intel' });
  const filterRailLabels = useFilterRailLabels();
  const { t } = useTranslation();
  const search = Route.useSearch() as IntelSearch;
  const view: i.IntelView = search.view ?? 'map';

  const filterState: f.FilterState = useMemo(() => {
    if (!search.f) return {};
    try {
      return decode(search.f);
    } catch {
      return {};
    }
  }, [search.f]);

  const sort: SortState = useMemo(() => {
    const raw = search.sort ?? 'occurred_at:desc';
    const [field, dir] = raw.split(':');
    return { field: field || 'occurred_at', dir: dir === 'asc' ? 'asc' : 'desc' };
  }, [search.sort]);

  const setFilterState = (next: f.FilterState) => {
    const enc = encode(next);
    const fParam = enc.kind === 'inline' ? enc.value || undefined : undefined;
    navigate({ search: (prev) => ({ ...prev, f: fParam }) });
  };
  const setSort = (next: SortState) =>
    navigate({ search: (prev) => ({ ...prev, sort: `${next.field}:${next.dir}` }) });
  const setView = (next: i.IntelView) =>
    navigate({ search: (prev) => ({ ...prev, view: next === 'map' ? undefined : next }) });
  const groupBy: GroupBy = search.group ?? 'flat';
  const setGroupBy = (next: GroupBy) =>
    navigate({ search: (prev) => ({ ...prev, group: next === 'flat' ? undefined : next }) });

  // `/` activates the IntentBar NL composer.
  const [nlEditing, setNlEditing] = useState(false);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/i.test(target.tagName)) return;
      if (e.key === '/' && !nlEditing) {
        e.preventDefault();
        setNlEditing(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nlEditing]);

  /* ────────────────────────────────────────────────────────────────────────
   *  Live event ring buffer for AlertTape. The SDK pushes new live signals
   *  here; AlertTape derives chips from the array.
   * ──────────────────────────────────────────────────────────────────────── */

  const [liveEvents, setLiveEvents] = useState<i.IntelSignal[]>([]);
  const [tapePaused, setTapePaused] = useState(false);
  const tenantId = useAuth((st) => st.me?.tenant_id ?? null);

  /* ────────────────────────────────────────────────────────────────────────
   *  Data — Phase B-3: `useIntelFeed` is the SSE-backed feed reader. Initial
   *  pull through `/v1/intel/signals`, live updates over SSE, IDB cache so
   *  reload renders instantly. The hook applies the client-side filter
   *  internally; the page just hands it `filterState`.
   * ──────────────────────────────────────────────────────────────────────── */

  const token = readAccessToken() ?? '';
  const feed = useIntelFeed(filterState, {
    sseUrl: `${API_BASE_URL}/v1/realtime/intel/sse`,
    pullUrl: `${API_BASE_URL}/v1/intel/signals`,
    token,
    onLiveEvent: (incoming) => {
      setLiveEvents((prev) => {
        const next = [...incoming, ...prev];
        return next.length > LIVE_EVENT_BUFFER ? next.slice(0, LIVE_EVENT_BUFFER) : next;
      });
    },
  });
  const signals = feed.rows;
  const isLoading = feed.status === 'connecting' && feed.rows.length === 0;
  // Source facets can come from the loaded sample (every signal has a source),
  // but signal_type counts MUST come from the server: the map feed restricts
  // to has_geom + limit 200, so geomless types (SAN sanctions, ~950k rows)
  // are absent from `signals` and would render as "0" in the rail. The
  // server facet counts the full filtered set regardless of geometry.
  const signalTypeFacet = useIntelSignalTypeFacet(filterState);
  const facetCounts = useMemo(
    () => ({ ...bucketCounts(signals), signal_type: signalTypeFacet }),
    [signals, signalTypeFacet],
  );

  // Non-geo results card. OFAC sanctions (SAN) are entity-keyed — no
  // coordinates — so they NEVER plot on the map. When the operator selects a
  // geomless type (alone OR stacked with a geo type like earthquakes), dock the
  // right-side results list so those matches are still visible.
  //
  // We drive this off the SELECTION, not off how full the map sample is.
  // Earlier saturation heuristics (signals.length / plottable count vs a 200
  // cap) were fragile: stacking a geo type (earthquakes) filled the 200-row
  // feed and wrongly hid the card, and a stale IDB cache of geomless rows made
  // the card flash-then-vanish on refresh. The only thing that actually decides
  // "are there matches that can't go on the map" is whether a geomless type is
  // selected — so use that directly.
  //
  // The signal_type facet excludes the signal_type filter from its own counts
  // (so selecting one type doesn't zero the others — see useIntelSignalTypeFacet);
  // sum only the selected buckets to get the count matching the CURRENT filter.
  const matchedTotal = useMemo(() => {
    const raw = filterState.signal_type;
    const selected = Array.isArray(raw)
      ? new Set(raw.filter((x): x is string => typeof x === 'string'))
      : typeof raw === 'string' && raw.length > 0
        ? new Set([raw])
        : null;
    return signalTypeFacet.reduce(
      (sum, b) =>
        selected && selected.size > 0
          ? selected.has(b.value)
            ? sum + b.count
            : sum
          : sum + b.count,
      0,
    );
  }, [signalTypeFacet, filterState]);
  const selectedHasGeomlessType = useMemo(() => {
    const raw = filterState.signal_type;
    const sel = Array.isArray(raw) ? raw : typeof raw === 'string' && raw.length > 0 ? [raw] : [];
    return sel.some((t) => typeof t === 'string' && GEOMLESS_SIGNAL_TYPES.has(t));
  }, [filterState]);
  const matchesButNonePlottable = !isLoading && matchedTotal > 0 && selectedHasGeomlessType;

  // Only on the map view do we dock the inline results list (the other views
  // already render geomless rows). Gated so we never fire the extra request
  // when the map can plot the matches.
  const showGeomlessPanel = view === 'map' && matchesButNonePlottable;
  const geomless = useIntelGeomlessResults(filterState, showGeomlessPanel);

  /* ────────────────────────────────────────────────────────────────────────
   *  AOI lifecycle — driven by GeoPolygonFacet's "edit AOI" button +
   *  the MapView terra-draw flow. Both write into FilterState.aoi.
   * ──────────────────────────────────────────────────────────────────────── */

  const aoiValue = (filterState.aoi as GeoPolygonFacetValue | undefined) ?? { polygons: [] };
  const aois = aoiValue.polygons;

  const addAoi = (next: GeoPolygonEntry) => {
    // The ui-filters GeoPolygonEntry uses number[][][] but the contracts'
    // FilterValue union types coordinates as a positional tuple. Same data
    // shape on the wire — cast at the boundary.
    const polygons = [...aois, next] as unknown as f.GeoPolygonValue['polygons'];
    setFilterState({ ...filterState, aoi: { polygons } });
  };
  const removeAoi = (id: string) => {
    const polygons = aois.filter((p) => p.id !== id) as unknown as f.GeoPolygonValue['polygons'];
    setFilterState({ ...filterState, aoi: { polygons } });
  };

  /* ────────────────────────────────────────────────────────────────────────
   *  Map view — registry-driven; layer ids derived from signal_type filter
   *  for type-based layers, separate local state for type-less aggregates.
   * ──────────────────────────────────────────────────────────────────────── */

  // Type-less layers (heatmap, density, contracts-hex, war-zones) don't map
  // onto the `signal_type` facet — they have their own enable/disable state.
  // Initialized from each spec's `defaultEnabled` so new type-less layers
  // can opt out of always-on (contracts-hex is default-off).
  const [typelessEnabledIds, setTypelessEnabledIds] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        INTEL_LAYERS.filter((l) => l.signalTypes.length === 0 && (l.defaultEnabled ?? true)).map(
          (l) => l.id,
        ),
      ),
  );

  const enabledLayerIds = useMemo<ReadonlySet<string>>(
    () => layerIdsFromSignalTypes(filterState, typelessEnabledIds),
    [filterState, typelessEnabledIds],
  );

  // contracts-hex is the only layer that needs a network fetch; gate the
  // hook on its enabled state so we don't burn a request unless the user
  // turns the layer on. The same filterState the rail emits is posted in
  // the body; the server compiles the intersection of fields that exist
  // on awards + contracts (jurisdiction, agency, vendor, procurement
  // category/method, dates, status, title), so the hex density narrows
  // when the user selects those facets. Intel-only fields (signal_type,
  // severity, naics) are silently ignored — they don't apply to contracts.
  // T11: the active contracts overlay is now `contracts-region` (a region
  // choropleth fed by /v1/map/regions from inside MapView). The legacy
  // centroid heatmap (`contracts-hex` + /v1/map/contracts) is unregistered;
  // this gate keys off the new id so the legacy fetch only fires if the
  // deprecated layer is ever explicitly re-enabled. The `contracts` array is
  // still threaded through for one release for any consumer of the old layer.
  const contractsLayerEnabled = enabledLayerIds.has('contracts-region');
  const { contracts } = useContractsMap(contractsLayerEnabled, filterState);

  // Basemap variant (T3) — defaults to 'terrain' per locked D-AS1. Persisted
  // in localStorage so the user's preference survives reload. Key matches
  // the existing intel-namespaced convention (`eid:intel:*`).
  const [basemapVariant, setBasemapVariant] = useState<'terrain' | 'sat' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'terrain';
    try {
      const v = window.localStorage.getItem('eid:intel:basemap');
      if (v === 'terrain' || v === 'sat' || v === 'dark') return v;
    } catch {
      /* private mode / no storage */
    }
    return 'terrain';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('eid:intel:basemap', basemapVariant);
    } catch {
      /* ignore */
    }
  }, [basemapVariant]);

  // Legend's "Active Layers" rows toggle either the `signal_type` filter
  // (for type-based layers, keeping the legend and EntityScreen filter rail
  // in lockstep — Iron Rule 11) or the typeless enabled set (for aggregate
  // layers like contracts-hex, heatmap, density, war-zones).
  const onToggleLayerFromLegend = (layerId: string) => {
    const spec = INTEL_LAYERS.find((l) => l.id === layerId);
    if (!spec) return;
    if (spec.signalTypes.length === 0) {
      setTypelessEnabledIds((prev) => {
        const next = new Set(prev);
        if (next.has(layerId)) next.delete(layerId);
        else next.add(layerId);
        return next;
      });
      return;
    }
    const types: readonly string[] = spec.signalTypes;
    const currentRaw = filterState.signal_type;
    const current: string[] = Array.isArray(currentRaw)
      ? currentRaw.filter((x): x is string => typeof x === 'string')
      : [];
    // Toggle-to-show model (founder spec): `signal_type` holds the types
    // CURRENTLY SHOWN; empty = nothing shown. Clicking a layer is a plain
    // toggle — add its types (show) or remove them (hide). NEVER enumerate
    // "every other type" (that made clicking one layer light up all of them).
    const next: string[] = types.every((t) => current.includes(t))
      ? current.filter((t) => !types.includes(t))
      : Array.from(new Set([...current, ...types]));
    // Clearing the field requires omission (FilterState record disallows
    // undefined values). Build the next state without the key when empty.
    const { signal_type: _drop, ...rest } = filterState;
    void _drop;
    setFilterState(next.length > 0 ? { ...rest, signal_type: next } : rest);
  };

  // Region drill-down: clicking a `contracts-region` choropleth polygon
  // toggles `filterState.region_id` to that region's `regions.id` (uuid).
  // Re-clicking the same region clears the filter.
  //
  // We filter by region_id → SPATIAL intersect (server resolves the uuid to
  // `regions.geom` and intersects signal geometry), NOT by the region's
  // display name. The name path was the bug: the data plane stores
  // `payload->>'action_country'` as a FIPS 10-4 code (US, GM, KS…) which
  // matches neither the display name ("United States") nor the region's
  // ISO-2 — so `region = name` matched 0 rows and every facet zeroed.
  // Spatial-by-geometry works at ANY admin level (country / state /
  // province) and narrows by area, so a province click no longer collapses
  // onto un-matchable country-level data. The whole page (result list, map
  // metric fetch, charts) re-reacts via the existing filter chain.
  const handleRegionClick = (props: RegionMetric) => {
    const clicked = (props.region_id ?? '').trim();
    if (!clicked) return;
    const current = filterState.region_id;
    const currentStr = typeof current === 'string' ? current : '';
    const { region_id: _drop, ...rest } = filterState;
    void _drop;
    if (currentStr === clicked) {
      setFilterState(rest);
    } else {
      setFilterState({ ...rest, region_id: clicked });
    }
  };

  const mapBody = (
    // Must replicate MapView's own layout contract: it fills its parent via
    // `flex: 1` (see MapView root), so the overlay wrapper has to be a growing
    // flex container, NOT `height: 100%` (which collapses to 0 in the column
    // flex above and makes deck.gl render nothing). position:relative anchors
    // the geomless nudge.
    <div
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
      }}
    >
      <MapView
        signals={signals}
        contracts={contracts}
        aois={aois}
        onAoiAdd={addAoi}
        onAoiRemove={removeAoi}
        enabledLayerIds={enabledLayerIds}
        onToggleLayer={onToggleLayerFromLegend}
        paused={tapePaused}
        onPauseToggle={() => setTapePaused((p) => !p)}
        filterState={filterState}
        basemapVariant={basemapVariant}
        onBasemapVariantChange={setBasemapVariant}
        onRegionClick={handleRegionClick}
      />
      {showGeomlessPanel ? (
        <div
          role="region"
          aria-label="Results without a map location"
          style={{
            position: 'absolute',
            // Sit BELOW the map HUD (zoom/AOI/pause are at top:12 right:12,
            // zIndex 30) and cap the height so the panel clears the top
            // controls and the bottom info button / scrubber. The list scrolls
            // internally.
            top: 56,
            right: 12,
            width: 380,
            maxWidth: 'calc(100% - 24px)',
            maxHeight: 'calc(100% - 112px)',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(10, 14, 20, 0.94)',
            border: '1px solid rgba(120, 150, 200, 0.3)',
            borderRadius: 6,
            font: '12px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
            color: '#c8d4e6',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(120,150,200,0.2)' }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
              {matchedTotal.toLocaleString()} results
              {matchedTotal > geomless.rows.length && !geomless.isLoading
                ? `  ·  showing first ${geomless.rows.length}`
                : ''}
            </div>
            <div style={{ color: '#8a99b0', fontSize: 11, marginTop: 2 }}>
              No map location (e.g. sanctions are entity-based, not geographic).
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {geomless.isLoading ? (
              <div style={{ padding: 14, color: '#8a99b0' }}>Loading results…</div>
            ) : geomless.rows.length === 0 ? (
              <div style={{ padding: 14, color: '#8a99b0' }}>No results.</div>
            ) : (
              geomless.rows.map((s) => (
                <div
                  key={s.id}
                  style={{
                    padding: '8px 14px',
                    borderBottom: '1px solid rgba(120,150,200,0.1)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      color: '#7e8ca3',
                      fontSize: 10,
                      marginBottom: 3,
                    }}
                  >
                    <span>
                      {s.source} · {s.signal_type}
                    </span>
                    <span>{s.occurred_at.slice(0, 10)}</span>
                  </div>
                  <div style={{ color: '#dce6f5' }}>{gdeltCodes.signalTitle(s)}</div>
                </div>
              ))
            )}
          </div>
          <div
            style={{
              padding: '8px 14px',
              borderTop: '1px solid rgba(120,150,200,0.2)',
              display: 'flex',
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={() => setView('timeline')}
              style={{
                padding: '6px 14px',
                background: 'rgba(90, 130, 200, 0.25)',
                border: '1px solid rgba(120, 150, 200, 0.5)',
                borderRadius: 4,
                color: '#dce6f5',
                font: 'inherit',
                cursor: 'pointer',
              }}
            >
              Open timeline
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const timelineBody = (
    <Suspense fallback={<EmptyState reason="Loading timeline…" />}>
      <Timeline signals={signals} groupBy={groupBy} />
    </Suspense>
  );
  // Analytics view disabled for now (founder request 2026-06-04). `analytics`
  // is no longer offered in IntelViewTabs; route any stale ?view=analytics URL
  // to the map. Re-enable by restoring analyticsBody + the tab/shortcut.
  const innerView = view === 'timeline' ? timelineBody : mapBody;
  // W2-C: dock the news side panel to the right of the active view. Sits
  // between the EntityScreen body and the SuggestionRail (the AI rail
  // mounts in EntityScreen, outside this tree). Collapsible.
  const viewBody = (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {innerView}
      </div>
      <Suspense fallback={null}>
        <NewsSidePanel filterState={filterState} />
      </Suspense>
    </div>
  );

  // Group-by control — visible only on the Timeline view, sits in the
  // IntelViewTabs right slot alongside SavedView.
  const groupChip =
    view === 'timeline' ? <GroupBySelect value={groupBy} onChange={setGroupBy} /> : null;

  // SavedView — platform primitive (Iron Rule #11). Lives in the right slot
  // of IntelViewTabs so it's accessible from every view (map / timeline /
  // analytics) without any per-page UI.
  const tabsRightSlot = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {groupChip}
      <SavedView
        entity="INTEL_SIGNAL"
        filters={filterState}
        onLoad={setFilterState}
        fetcher={savedViewFetcher}
      />
    </span>
  );

  // T2 — Hierarchical filter for signal_type (canonical sub-category from
  // @eidollon/contracts/intel-domains). Reads/writes FilterState.signal_type;
  // the platform's IntentBar continues to render those chips automatically.
  //
  // Wire format: signal_type is the 3-letter ETL code (SAN/CFL/SUP/SEI/WEA/…).
  // UI surface: domain → subcategory taxonomy from intel-domains.
  // The maps in @eidollon/contracts bridge the two; this hook handles both
  // directions (read = code→subcat for display, write = subcat→code for the
  // API/URL).
  const hierarchicalSubcatValue = useMemo<ReadonlySet<string>>(() => {
    const raw = filterState.signal_type;
    const codes: string[] = Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === 'string')
      : typeof raw === 'string' && raw.length > 0
        ? [raw]
        : [];
    if (codes.length === 0) return new Set();
    // Translate codes → subcat keys for display. Codes with no 1:1 subcat
    // (CFL/OBL/POL/AVI/ANO) don't appear as checked subcats — they show up
    // only at the domain-total level. That's intentional; selecting them
    // happens via the chip composer / NL bar / direct URL, not the tree.
    const subcatByCode = iDom.SIGNAL_TYPE_TO_SUBCATEGORY;
    const out = new Set<string>();
    for (const code of codes) {
      const sub = subcatByCode[code as keyof typeof subcatByCode];
      if (sub) out.add(sub);
    }
    return out;
  }, [filterState.signal_type]);

  const setHierarchicalSubcat = (next: ReadonlySet<string>) => {
    // BUG-1 fix: write back round-trip-safely. The rail only owns the
    // 1:1-mapped codes (SAN/SEI/WEA/FIR/SUP); the legend toggle / chip composer
    // / URL can also put NON-rail codes (CFL/POL/OBL/AVI/ANO/NEW) into
    // `signal_type`. The old code rebuilt `signal_type` purely from the rail's
    // selection, silently dropping those — so a deselect couldn't return to the
    // unfiltered state. `nextSignalTypeFromSubcats` preserves the non-rail
    // codes and replaces only the rail-owned ones. Pure + unit-tested in
    // screens/intel/signal-type-writeback.test.ts.
    const codes = nextSignalTypeFromSubcats(filterState.signal_type, next);
    const { signal_type: _drop, ...rest } = filterState;
    setFilterState(codes.length > 0 ? { ...rest, signal_type: codes } : rest);
  };

  // Counts: facetCounts.signal_type is keyed by 3-letter codes. Project them
  // both onto subcategories (1:1 mappings only) AND onto domain totals (the
  // full code→domain map).
  const { subcatCountByKey, domainCountByKey } = useMemo(() => {
    const subMap: Record<string, number> = {};
    const domMap: Record<string, number> = {};
    const subcatByCode = iDom.SIGNAL_TYPE_TO_SUBCATEGORY;
    const domainByCode = iDom.SIGNAL_TYPE_TO_DOMAIN;
    for (const b of facetCounts.signal_type ?? []) {
      const code = b.value;
      const sub = subcatByCode[code as keyof typeof subcatByCode];
      if (sub) subMap[sub] = (subMap[sub] ?? 0) + b.count;
      const dom = domainByCode[code as keyof typeof domainByCode];
      if (dom) domMap[dom] = (domMap[dom] ?? 0) + b.count;
    }
    return { subcatCountByKey: subMap, domainCountByKey: domMap };
  }, [facetCounts]);

  const hierarchicalDomains: HierarchicalDomainOption[] = useMemo(() => {
    const domains = ['conflict', 'disaster', 'trade'] as const;
    return domains.map((dKey) => {
      const subKeys = Object.entries(iDom.SUBCATEGORY_PARENT)
        .filter(([_, parent]) => parent === dKey)
        // "Collapse to real capability": only render subcategory rows that
        // can carry a true count today (a 1:1 wire code). Unbacked slots —
        // notably the 5 CFL conflict subcategories that need the L4 classifier
        // backfill — are hidden, and their volume is surfaced at the domain
        // total instead (see SIGNAL_TYPE_TO_DOMAIN). No permanent "—" rows.
        // Re-enabling a hidden subcat is a config flip in intel-domains.ts
        // (SUBCATEGORY_VISIBLE_IN_RAIL) once the classifier populates it.
        .filter(
          ([sub]) =>
            iDom.SUBCATEGORY_VISIBLE_IN_RAIL[sub as keyof typeof iDom.SUBCATEGORY_VISIBLE_IN_RAIL],
        )
        .map(([sub]) => sub);
      const subcategories = subKeys.map((s) => ({
        key: s,
        label: t(`intelSubcategory.${s}`, s),
        count: subcatCountByKey[s] ?? 0,
      }));
      return {
        key: dKey,
        label: t(`intelDomain.${dKey}`, dKey),
        // Domain total comes from the code→domain map, NOT the sum of
        // subcat counts — otherwise CFL (which has no subcat) wouldn't
        // contribute to the Conflict total.
        count: domainCountByKey[dKey] ?? 0,
        subcategories,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcatCountByKey, domainCountByKey, t]);

  const railTopSlot = (
    <div style={{ borderBottom: '1px solid var(--bd)' }}>
      <HierarchicalFilter
        domains={hierarchicalDomains}
        value={hierarchicalSubcatValue}
        onChange={setHierarchicalSubcat}
        clearAllLabel={t('filterRail.clr', 'Clear all')}
        selectionSummary={(n) =>
          n === 0
            ? t('intelHierarchicalFilter.none', 'No subcategories selected')
            : t('intelHierarchicalFilter.selected', '{{count}} selected', { count: n })
        }
        defaultExpanded={new Set(['conflict', 'disaster', 'trade'])}
      />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <EntityScreen<i.IntelSignal>
        {...filterRailLabels}
        schema={fc.intelFilterSchema}
        state={filterState}
        onStateChange={setFilterState}
        sort={sort}
        onSortChange={setSort}
        rows={[]}
        rowKey={(s) => s.id}
        columns={[]}
        count={signals.length}
        total={feed.total}
        loading={isLoading}
        ai={{
          suggestions: [],
          coldStart: true,
          onOpen: () => {
            /* phase D */
          },
          onDismiss: () => {
            /* phase D */
          },
          onMuteCategory: () => {
            /* phase D */
          },
        }}
        facetCounts={facetCounts}
        rangePresets={{ time_window: TIME_PRESETS }}
        geoEditors={{
          aoi: () => {
            // Same window event the GeoPolygonFacet bridge dispatches; the
            // MapView is the single owner of the actual draw flow.
            window.dispatchEvent(
              new CustomEvent('intel.openAoiTool', { detail: { field: 'aoi' } }),
            );
          },
        }}
        nlEditing={nlEditing}
        onActivateNl={() => setNlEditing(true)}
        onCancelNl={() => setNlEditing(false)}
        topSlot={
          <IntelViewTabs view={view} onViewChange={setView} rightSlot={tabsRightSlot}>
            {viewBody}
          </IntelViewTabs>
        }
        // The Intel page owns its entire result column via IntelViewTabs
        // (MAP / TIMELINE / ANALYTICS each render their own body). The
        // platform table is suppressed for this surface.
        topSlotMode="grow"
        meta={{ backend: 'pgvector+postgis', fromCache: false }}
        railTopSlot={railTopSlot}
      />
      <AlertTape
        events={liveEvents}
        paused={tapePaused}
        onResume={() => setTapePaused(false)}
        tenantId={tenantId}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Filter helpers
 * ────────────────────────────────────────────────────────────────────────── */

function bucketCounts(rows: i.IntelSignal[]): Record<string, { value: string; count: number }[]> {
  const byType: Record<string, number> = {};
  const bySource: Record<string, number> = {};
  for (const r of rows) {
    byType[r.signal_type] = (byType[r.signal_type] ?? 0) + 1;
    bySource[r.source] = (bySource[r.source] ?? 0) + 1;
  }
  const toBuckets = (m: Record<string, number>) =>
    Object.entries(m).map(([value, count]) => ({ value, count }));
  return {
    signal_type: toBuckets(byType),
    source: toBuckets(bySource),
  };
}

/** Derive enabled layer-registry ids from the `signal_type` selection plus the
 *  explicit type-less enabled set. Toggle-to-show model (founder spec): a
 *  type-based layer is enabled ONLY when one of its types is selected — so an
 *  EMPTY selection shows NOTHING (off by default), and toggling a type on
 *  reveals exactly that type's markers. Type-less aggregate layers
 *  (density, heatmap, war-zones, contracts-hex) use the explicit set so
 *  analysts can flip them independently from the legend. */
function layerIdsFromSignalTypes(
  state: f.FilterState,
  typelessEnabledIds: ReadonlySet<string>,
): Set<string> {
  const selected = state.signal_type;
  const selectedSet =
    Array.isArray(selected) && selected.length > 0
      ? new Set<string>(selected.filter((x): x is string => typeof x === 'string'))
      : null;
  const out = new Set<string>();
  for (const spec of INTEL_LAYERS) {
    if (spec.signalTypes.length === 0) {
      if (typelessEnabledIds.has(spec.id)) out.add(spec.id);
      continue;
    }
    // Empty selection (selectedSet == null) → NOT enabled → nothing shown.
    if (selectedSet != null && spec.signalTypes.some((t) => selectedSet.has(t))) {
      out.add(spec.id);
    }
  }
  return out;
}

function GroupBySelect({ value, onChange }: { value: GroupBy; onChange: (next: GroupBy) => void }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
        }}
      >
        GROUP
      </span>
      <span style={{ display: 'inline-flex', border: '1px solid var(--bd-strong)' }}>
        {GROUP_OPTIONS.map((opt) => {
          const on = opt.id === value;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              aria-pressed={on}
              style={{
                height: 18,
                padding: '0 6px',
                background: on ? 'var(--primary-soft)' : 'transparent',
                color: on ? 'var(--primary)' : 'var(--fg-2)',
                border: 'none',
                borderRight: '1px solid var(--bd)',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </span>
    </span>
  );
}
